import React, { useContext, useEffect, useState } from 'react';
import { Box, CircularProgress, Collapse } from 'housecall-ui';
import Typography from 'housecall-ui/dist/DesignSystem/DataDisplay/Typography/Typography';
import Button from 'housecall-ui/dist/DesignSystem/Inputs/Buttons/Button';
import Grid from 'housecall-ui/dist/DesignSystem/Layout/Grid/Grid';
import { isBlank } from 'utils/isBlank';

import { withStripe } from 'components/Common/Form/Stripe/Utils';
import { useTrackEventsService } from 'components/Services/Analytics/useTrackEventsService';
import CreditCardForm from 'components/Settings/Billing/CreditCards/CreditCardForm';
import CreditCardFormProvider from 'components/Settings/Billing/CreditCards/CreditCardFormProvider';
import { getCreditCardIconImage } from 'components/Settings/Billing/CreditCards/CreditCardItem';
import { useCreditCards } from 'components/Settings/Billing/CreditCards/hooks/useCreditCards';
import useResetCreditCardForm from 'components/Settings/Billing/CreditCards/hooks/useResetCreditCardForm';
import useSubmitToStripe from 'components/Settings/Billing/CreditCards/hooks/useSubmitToStripe';

import { PayWallContext, PayWallDispatchContext } from './PayWallContextProvider';
import { CIRCULAR_PROGRESS_BUTTON_SIZE, PAY_WALL_VERSION, useButtonProgressStyles } from './PayWallDialog';

type PayWallPaymentInformationProps = {
  stripe: Stripe;
};

const PayWallPaymentInformation = ({ stripe }: PayWallPaymentInformationProps) => {
  const { trackEvent, trackButtonClicked } = useTrackEventsService();

  const classes = useButtonProgressStyles({ circularProgressSize: CIRCULAR_PROGRESS_BUTTON_SIZE });

  const { data: creditCards, isLoading: isLoadingCards, isFetching } = useCreditCards();

  const primaryCreditCard = creditCards?.find((card) => card.primary === true);

  const [showSpinner, setShowSpinner] = useState(false);

  const { showCreditCardForm } = useContext(PayWallContext);
  const { setShowCreditCardForm } = useContext(PayWallDispatchContext);

  const resetCreditCardForm = useResetCreditCardForm();

  useEffect(() => {
    setShowCreditCardForm(isBlank(primaryCreditCard));
  }, [primaryCreditCard, setShowCreditCardForm]);

  const handleCloseCreditCardForm = () => {
    setShowCreditCardForm(false);
    resetCreditCardForm();
    trackButtonClicked({ buttonName: 'Cancel payment change PayWall', payWallVersion: PAY_WALL_VERSION });
  };

  const submitToStripe = useSubmitToStripe(
    stripe,
    true, // for now it should always set new credit card as primary
    () => {
      setShowSpinner(false);
      handleCloseCreditCardForm();
      trackEvent('PayWall payment information updated', { payWallVersion: PAY_WALL_VERSION });
    },
    () => {
      setShowSpinner(false);
    },
    'paywall payment information'
  );

  const isSubmitDisabled = showSpinner || isLoadingCards;

  return (
    <Grid container direction="column" spacing={2} data-testid="paywall-payment-information">
      <Grid item xs={12}>
        <Typography variant="h3">Payment</Typography>
      </Grid>
      <Grid item xs={12}>
        {primaryCreditCard && (
          <Collapse in={!showCreditCardForm && !isFetching}>
            <Grid direction="column" container spacing={2} alignItems="baseline">
              <Grid
                item
                container
                alignItems="center"
                spacing={1}
                data-testid="paywall-payment-information__card-display"
              >
                <Grid item>
                  <Box width={48}>
                    <img
                      width="100%"
                      src={getCreditCardIconImage(primaryCreditCard.brand)}
                      alt={primaryCreditCard.brand}
                      data-testid="credit-card-item__provider-logo"
                    />
                  </Box>
                </Grid>
                <Grid item>
                  <Typography>
                    <strong>{primaryCreditCard.brand}</strong>
                    {' ending in '}
                    <strong>{primaryCreditCard.last4}</strong>
                  </Typography>
                </Grid>
              </Grid>
              <Grid item container>
                <Button
                  color="primary"
                  variant="outlined"
                  data-testid="paywall-payment-information__change-payment"
                  onClick={() => {
                    setShowCreditCardForm(true);
                    trackButtonClicked({ buttonName: 'Change payment PayWall', payWallVersion: PAY_WALL_VERSION });
                  }}
                >
                  Change payment
                </Button>
              </Grid>
            </Grid>
          </Collapse>
        )}
        <Collapse in={showCreditCardForm} timeout={0}>
          {showCreditCardForm && <CreditCardForm />}
          <Box mt={3}>
            <Typography variant="caption">
              This card will be saved to your account as a primary form of payment. To make changes, please visit the
              Billing section of your account. You authorize us to charge this card and any other forms of payment you
              have on file with us if your primary form of payment fails.
            </Typography>
          </Box>
          <Box mt={2} display="flex" justifyContent="flex-end">
            {!isBlank(primaryCreditCard) && (
              <Button
                variant="text"
                onClick={handleCloseCreditCardForm}
                data-testid="paywall-payment-information__cancel-cc-edit"
              >
                Cancel
              </Button>
            )}
            <Box ml={1} className={classes.wrapper}>
              <Button
                color="primary"
                disabled={isSubmitDisabled}
                variant="outlined"
                onClick={() => {
                  setShowSpinner(true);
                  submitToStripe();
                }}
              >
                Add Credit Card
                {showSpinner && (
                  <CircularProgress className={classes.buttonProgress} size={CIRCULAR_PROGRESS_BUTTON_SIZE} />
                )}
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Grid>
    </Grid>
  );
};

const PayWallPaymentInformationWrapper = (props: PayWallPaymentInformationProps) => (
  <CreditCardFormProvider>
    <PayWallPaymentInformation {...props} />
  </CreditCardFormProvider>
);

export default withStripe()(PayWallPaymentInformationWrapper);
